import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { login } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { Button } from '@/ui/Button'
import { Input } from '@/ui/Input'

export function LoginPage() {
  const navigate = useNavigate()
  const { user, loading: authLoading, setAuthenticatedUser } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (!authLoading && user) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await login(email, password)
      if (data.status === 'authenticated') {
        setAuthenticatedUser(data.user)
        navigate('/', { replace: true })
      } else {
        setError('Unexpected login response')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen bg-ink overflow-hidden grain-overlay">
      {/* Floating organic blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-32 w-[32rem] h-[32rem] opacity-30 animate-float-slow"
        style={{ background: 'radial-gradient(circle, var(--gradient-bloom-1) 0%, transparent 65%)', borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/3 -left-48 w-[28rem] h-[28rem] opacity-25 animate-float"
        style={{ background: 'radial-gradient(circle, var(--gradient-bloom-2) 0%, transparent 65%)', borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%', animationDelay: '2s' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 right-1/4 w-[36rem] h-[36rem] opacity-20 animate-float-slow"
        style={{ background: 'radial-gradient(circle, var(--color-ember) 0%, transparent 60%)', borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%', animationDelay: '4s' }}
      />

      {/* Tiny brand mark top-left */}
      <div className="absolute top-8 left-8 lg:top-10 lg:left-12 flex items-baseline gap-2 animate-fade-in">
        <span className="w-2 h-2 rounded-full bg-ember" />
        <span className="font-display italic text-base text-cream/70">beauty</span>
      </div>

      <div className="relative min-h-screen grid lg:grid-cols-2 max-w-[1400px] mx-auto">
        {/* Left — editorial hero */}
        <div className="flex items-center px-8 lg:px-16 pt-28 pb-8 lg:py-20">
          <div className="max-w-lg">
            <h1
              className="font-display text-6xl lg:text-[5.5rem] font-medium text-cream leading-[0.95] tracking-tight mb-8 animate-fade-in"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}
            >
              beauty<br />
              <span className="italic text-ember">one-oh-one</span>
            </h1>
            <p className="font-display italic text-xl text-cream/80 leading-snug mb-10 max-w-sm animate-fade-in stagger-2">
              A personal beauty consultant — quiet, considered, always on.
            </p>

            <div className="flex items-center gap-4 mb-10 max-w-xs animate-fade-in stagger-3">
              <span className="flex-1 h-px bg-haze/60" />
              <span className="w-2 h-2 rounded-full bg-ember/60" />
              <span className="flex-1 h-px bg-haze/60" />
            </div>

            <p className="text-base text-mute leading-relaxed max-w-sm animate-fade-in stagger-4">
              Ask about skincare, ingredients, routines, or anything you'd like a second opinion on. No dashboards. No jargon. Just conversation.
            </p>
          </div>
        </div>

        {/* Right — form panel */}
        <div className="flex items-center justify-center lg:justify-start px-8 lg:pr-16 lg:pl-8 pb-16 lg:py-20">
          <div className="w-full max-w-md animate-fade-in stagger-3">
            <div
              className="glass rounded-3xl p-10 lg:p-12"
              style={{ boxShadow: 'var(--shadow-form)' }}
            >
              <div className="mb-10">
                <h2 className="font-display text-2xl font-medium text-cream tracking-tight mb-2">
                  Welcome back
                </h2>
                <p className="text-sm text-mute italic font-display">
                  Sign in to continue the conversation.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-7">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-cream mb-2.5">
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    className="text-base placeholder-mute/50"
                    placeholder="you@example.com"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-cream mb-2.5">
                    Password
                  </label>
                  <Input
                    id="password"
                    type="password"
                    className="text-base placeholder-mute/50"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                {error && (
                  <div className="text-sm text-ember border-l-[3px] border-ember pl-4 py-2 animate-fade-in bg-ember/5 rounded-r-lg">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  loading={loading}
                  size="lg"
                  className="w-full gap-2"
                >
                  <span>{loading ? 'Signing in' : 'Sign in'}</span>
                  {!loading && (
                    <span className="transition-transform group-hover:translate-x-0.5">→</span>
                  )}
                </Button>
              </form>
            </div>

            <p className="text-center text-sm text-mute/70 mt-8">
              Need an invitation?{' '}
              <a href="mailto:hello@beauty101.app" className="text-ember hover:underline underline-offset-2 transition-colors">
                Reach out
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 left-0 right-0 text-center">
        <p className="font-display italic text-xs text-mute/50 tracking-wide">
          a quiet place · est. 2025
        </p>
      </div>
    </div>
  )
}
